import type { PlanetDef } from '@playnets/sim';

/**
 * PLANET PERSONALITIES
 * ====================
 *
 * A planet is a character class, and the brief is that it should read as one
 * before the player has fired a shot. Four channels carry that:
 *
 *   1. MECHANICS  — passive + ultimate + slot layout. What it's good at.
 *   2. MOTION     — natural spin (speed *and direction*) and ring inertia.
 *                   Ignis grinds one way; Machina whips the other.
 *   3. BODY       — a procedural recipe, not a sprite. The feature ring around
 *                   the core is the strongest silhouette read: shards, cells,
 *                   crystals, plates, vents, lattice.
 *   4. VOICE      — placard lines in the poster margin. Six distinct registers:
 *                   patient, furious, glacial, clipped, vast, austere.
 *
 * Temperament ties channels 2–4 together: a restless planet is restless in its
 * crust motion, its pulse rate, and the rhythm of what it says.
 */

const terra: PlanetDef = {
  id: 'terra',
  name: 'Terra',
  designation: 'SOL-III · CLASS M',
  epithet: 'Forgiving. Teaches the game.',
  bio: 'Temperate, patient, and generous with what it gives back. Terra absorbs a mistake and hands you the juice to fix it. Every other planet in the catalogue is a specialist; this one is a place to learn what the ring is for.',
  temperament: {
    restlessness: 0.25,
    jitter: 0.1,
    reactivity: 0.4,
    pulsePeriod: 4.2,
    traits: ['steady', 'generous', 'unhurried'],
  },
  passive: {
    id: 'green-yield',
    label: 'Green Yield',
    text: '+1 juice from every drop.',
    juiceBonus: 1,
  },
  ultimate: {
    id: 'aurora-pulse',
    name: 'Aurora Pulse',
    kind: 'pulse',
    cooldown: 45,
    aimed: false,
    text: 'An expanding ring of light. Everything it touches stops for two seconds.',
    damage: 26,
    spread: Math.PI * 2,
    duration: 2,
    groundDuration: 0,
    groundDps: 0,
    count: 1,
    knockback: 0,
    frontSpeed: 330,
  },
  exclusive: 'shield-pylon',
  slots: { surface: 4, ring: 3 },
  spin: 0.055,
  ringInertia: 1,
  coreHp: 110,
  palette: {
    halo: 0xcfe3d8,
    crust: 0x4a7c74,
    crustDark: 0x2f5b57,
    crustLight: 0x6fa094,
    feature: 0x35705c,
    featureDark: 0x24523f,
    featureLight: 0x8fc9a4,
    core: 0xe4c16a,
    coreRim: 0xc9a227,
    accent: 0xff6b35,
  },
  body: {
    feature: 'plates',
    featureCount: 14,
    featureReach: 0.42,
    featureChaos: 0.25,
    coreRatio: 0.46,
    shellWobble: 0.06,
    shellLobes: 7,
    speckle: 0.25,
    halo: 0.16,
    bands: 0,
    face: 'calm',
  },
  voice: {
    onWave: ['Bearings settling.', 'They are forming up. Take your time.', 'Watch the corridor.'],
    onUltimate: ['Aurora up.', 'Hold still, all of you.'],
    onHurt: ['Crust breached. It will hold.', 'That one got through.'],
    onClear: ['Clear. Spend it well.', 'Nothing left on the ring.'],
  },
  evolutions: {
    l4: [
      {
        id: 'verdant',
        name: 'Verdant',
        text: 'Every drop is worth +2 instead of +1. The crust greens over.',
        passive: { juiceBonus: 2, text: '+2 juice from every drop.' },
        palette: { crust: 0x3f8a5f, crustLight: 0x76c08a, feature: 0x2f6f4a },
        body: { speckle: 0.45, featureCount: 18 },
      },
      {
        id: 'tempered',
        name: 'Tempered',
        text: 'Aurora Pulse also damages hard, but stuns for half as long.',
        ultimate: { damage: 68, duration: 1 },
        palette: { crust: 0x5c6f7c, crustDark: 0x3a4a55, feature: 0x7d8f9c },
        body: { shellWobble: 0.02, featureReach: 0.3 },
      },
    ],
    l8: [
      {
        id: 'biosphere',
        name: 'Biosphere',
        text: 'The pulse holds everything for four seconds instead of two.',
        ultimate: { duration: 4, cooldown: 58 },
        body: { halo: 0.28, speckle: 0.6 },
      },
      {
        id: 'second-dawn',
        name: 'Second Dawn',
        text: 'The pulse fires twice, four seconds apart.',
        ultimate: { count: 2, cooldown: 52 },
        palette: { accent: 0xffd166, coreRim: 0xffd166 },
      },
    ],
  },
};

const ignis: PlanetDef = {
  id: 'ignis',
  name: 'Ignis',
  designation: 'SOL-IV · CLASS T',
  epithet: 'Commit to a side. Punish it.',
  bio: 'A world with no interest in coverage. Ignis wants you to pick a bearing, stack it, and dare the wave to come from anywhere else — and when it does, the vent opens and the argument ends. Impatient in the crust, impatient in the guns.',
  temperament: {
    restlessness: 0.85,
    jitter: 0.55,
    reactivity: 1,
    pulsePeriod: 1.4,
    traits: ['furious', 'committed', 'unsubtle'],
  },
  passive: {
    id: 'hot-crust',
    label: 'Hot Crust',
    text: 'Surface structures deal +20% damage.',
    surfaceDamageMult: 1.2,
  },
  ultimate: {
    id: 'eruption',
    name: 'Eruption',
    kind: 'cone',
    cooldown: 50,
    aimed: true,
    text: 'A 90° cone of everything the mantle has. Leaves burning ground behind.',
    damage: 120,
    spread: Math.PI / 2,
    duration: 0,
    groundDuration: 5,
    groundDps: 22,
    count: 1,
    knockback: 0,
    frontSpeed: 0,
  },
  exclusive: 'magma-vent',
  slots: { surface: 5, ring: 2 },
  spin: 0.11,
  ringInertia: 1.15,
  coreHp: 100,
  palette: {
    halo: 0xf4b942,
    crust: 0xd4552a,
    crustDark: 0xa03a1c,
    crustLight: 0xe8763c,
    feature: 0xe0632c,
    featureDark: 0x9c3617,
    featureLight: 0xf7c948,
    core: 0xffd429,
    coreRim: 0xf29d1f,
    accent: 0xff6b35,
  },
  body: {
    feature: 'cells',
    featureCount: 22,
    featureReach: 0.55,
    featureChaos: 0.45,
    coreRatio: 0.4,
    shellWobble: 0.14,
    shellLobes: 5,
    speckle: 0,
    halo: 0.1,
    bands: 0,
    face: 'fierce',
  },
  voice: {
    onWave: ['Pick a side.', 'Pressure rising.', 'Something is coming. Choose.'],
    onUltimate: ['ERUPT.', 'Open the vent.', 'Burn it.'],
    onHurt: ['Hull. Not the mantle.', 'You let it in.'],
    onClear: ['Ash.', 'Done. Next.'],
  },
  evolutions: {
    l4: [
      {
        id: 'pyre',
        name: 'Pyre',
        text: 'Eruption damage +40%, cone narrows to 60°.',
        ultimate: { damage: 168, spread: Math.PI / 3 },
        palette: { crust: 0xe04a1c, featureLight: 0xffe066, core: 0xffffff },
        body: { featureReach: 0.68, featureChaos: 0.55 },
      },
      {
        id: 'ashen',
        name: 'Ashen',
        text: 'Eruption damage −20%, burning ground lasts three times longer.',
        ultimate: { damage: 96, groundDuration: 15, groundDps: 20 },
        palette: {
          crust: 0x6b5148,
          crustDark: 0x453029,
          crustLight: 0x8d6c5e,
          feature: 0x8a4a2c,
          featureLight: 0xc47a3a,
        },
        body: { speckle: 0.35, featureChaos: 0.6 },
      },
    ],
    l8: [
      {
        id: 'cataclysm',
        name: 'Cataclysm',
        text: 'Eruption knocks survivors back to the spawn radius.',
        ultimate: { knockback: 220 },
        body: { shellWobble: 0.22, shellLobes: 4 },
      },
      {
        id: 'twin-vent',
        name: 'Twin Vent',
        text: 'Two 45° cones fire on opposite bearings.',
        ultimate: { count: 2, spread: Math.PI / 4 },
        body: { featureCount: 30 },
      },
    ],
  },
};

const glacia: PlanetDef = {
  id: 'glacia',
  name: 'Glacia',
  designation: 'SOL-VI · CLASS P',
  epithet: 'Buys time. Rewards patience.',
  bio: 'Nothing on Glacia is in a hurry, including the things attacking it. The crust turns so slowly you can build against a bearing and trust it to stay there, and when the wave finally breaks, the whole sky stops for three seconds while you decide what to do about it.',
  temperament: {
    restlessness: 0.12,
    jitter: 0.05,
    reactivity: 0.3,
    pulsePeriod: 6.5,
    traits: ['glacial', 'exact', 'unbothered'],
  },
  passive: {
    id: 'cold-approach',
    label: 'Cold Approach',
    text: 'Enemies arrive 10% slower.',
    enemySpeedMult: 0.9,
  },
  ultimate: {
    id: 'deep-freeze',
    name: 'Deep Freeze',
    kind: 'freeze',
    cooldown: 55,
    aimed: false,
    text: 'The whole sky freezes for three seconds. Frozen things take triple damage, and anything badly hurt shatters on the thaw.',
    damage: 30,
    spread: Math.PI * 2,
    duration: 3,
    groundDuration: 0,
    groundDps: 0,
    count: 1,
    knockback: 0,
    frontSpeed: 420,
  },
  exclusive: 'cryo-lance',
  slots: { surface: 4, ring: 3 },
  spin: 0.018,
  ringInertia: 0.9,
  coreHp: 120,
  palette: {
    halo: 0xe8f4f8,
    crust: 0xa8c4d4,
    crustDark: 0x7d9fb5,
    crustLight: 0xd6e7f0,
    feature: 0x4a7c8c,
    featureDark: 0x35606f,
    featureLight: 0xeaf6fb,
    core: 0x4f7fa8,
    coreRim: 0x35607f,
    accent: 0x7fd4e8,
  },
  body: {
    // The ice-shard silhouette: long spines reaching in toward a deep blue core.
    feature: 'shards',
    featureCount: 13,
    featureReach: 0.62,
    featureChaos: 0.5,
    coreRatio: 0.5,
    shellWobble: 0.09,
    shellLobes: 6,
    speckle: 0.75,
    halo: 0.22,
    bands: 0,
    face: 'sleepy',
  },
  voice: {
    onWave: ['They are early.', 'Let them come to us.', 'No need to rush.'],
    onUltimate: ['Everything stops.', 'Hold.'],
    onHurt: ['A crack. It will close.', 'Noted.'],
    onClear: ['Quiet again.', 'As expected.'],
  },
  evolutions: {
    l4: [
      {
        id: 'permafrost',
        name: 'Permafrost',
        text: 'Enemies arrive 20% slower. The shell thickens.',
        passive: { enemySpeedMult: 0.8, text: 'Enemies arrive 20% slower.' },
        body: { halo: 0.34, speckle: 0.9, shellWobble: 0.04 },
        palette: { halo: 0xffffff, crustLight: 0xffffff },
      },
      {
        id: 'blackice',
        name: 'Black Ice',
        text: 'Freeze lasts 2s instead of 3, but deals four times the damage.',
        ultimate: { duration: 2, damage: 120 },
        palette: {
          crust: 0x39566a,
          crustDark: 0x22384a,
          crustLight: 0x5d8299,
          core: 0x101c2a,
          coreRim: 0x2a465c,
          feature: 0x2b4557,
        },
      },
    ],
    l8: [
      {
        id: 'shatterpoint',
        name: 'Shatterpoint',
        text: 'The thaw itself deals splash damage where each target stood.',
        ultimate: { groundDuration: 2.5, groundDps: 30 },
        body: { featureCount: 20, featureChaos: 0.7 },
      },
      {
        id: 'long-winter',
        name: 'Long Winter',
        text: 'Freeze lasts five seconds. Cooldown rises to 70.',
        ultimate: { duration: 5, cooldown: 70 },
        body: { featureReach: 0.78 },
      },
    ],
  },
};

const machina: PlanetDef = {
  id: 'machina',
  name: 'Machina',
  designation: 'SOL-V · CLASS X (ARTIFICIAL)',
  epithet: 'Fast, fiddly, high ceiling.',
  bio: 'Not a planet so much as a machine that got large enough to hold an atmosphere. The ring answers a quarter faster than anything else in the catalogue, which means Machina can cover a feint nobody else can — and punishes you for every input you did not mean.',
  temperament: {
    restlessness: 0.7,
    jitter: 0.95,
    reactivity: 0.6,
    pulsePeriod: 0.9,
    traits: ['clipped', 'precise', 'twitchy'],
  },
  passive: {
    id: 'servo-ring',
    label: 'Servo Ring',
    text: 'The orbit ring rotates 25% faster.',
    ringSpeedMult: 1.25,
  },
  ultimate: {
    id: 'swarm-deploy',
    name: 'Swarm Deploy',
    kind: 'swarm',
    cooldown: 48,
    aimed: false,
    text: 'Six autonomous satellites for twenty seconds. They pick their own targets.',
    damage: 16,
    spread: 0,
    duration: 20,
    groundDuration: 0,
    groundDps: 0,
    count: 6,
    knockback: 0,
    frontSpeed: 0,
  },
  exclusive: 'drone-bay',
  slots: { surface: 3, ring: 4 },
  spin: -0.16,
  ringInertia: 0.75,
  coreHp: 90,
  palette: {
    halo: 0xd9d2c2,
    crust: 0x8a8f9a,
    crustDark: 0x5b616d,
    crustLight: 0xb9bfc9,
    feature: 0xc9a227,
    featureDark: 0x8f731a,
    featureLight: 0xf0d060,
    core: 0x2b2f3a,
    coreRim: 0xc9a227,
    accent: 0xffd166,
  },
  body: {
    feature: 'lattice',
    featureCount: 28,
    featureReach: 0.4,
    featureChaos: 0.08,
    coreRatio: 0.44,
    shellWobble: 0.02,
    shellLobes: 12,
    speckle: 0.1,
    halo: 0.08,
    bands: 0,
    face: 'machine',
  },
  voice: {
    onWave: ['Contacts. Two streams.', 'Bearing solution pending.', 'Wave inbound. Advise spin.'],
    onUltimate: ['Swarm away.', 'Six units. Twenty seconds.'],
    onHurt: ['Integrity down.', 'Hull fault, sector logged.'],
    onClear: ['Sector clear.', 'Nominal.'],
  },
  evolutions: {
    l4: [
      {
        id: 'overclock',
        name: 'Overclock',
        text: 'Ring speed +50%. Ring inertia drops away to nothing.',
        passive: { ringSpeedMult: 1.5, text: 'The orbit ring rotates 50% faster.' },
        palette: { feature: 0xff6b35, featureLight: 0xffd166 },
        body: { featureCount: 36, shellLobes: 16 },
      },
      {
        id: 'foundry',
        name: 'Foundry',
        text: 'Nine satellites instead of six, at reduced damage.',
        ultimate: { count: 9, damage: 12 },
        body: { featureReach: 0.52 },
      },
    ],
    l8: [
      {
        id: 'persistent-swarm',
        name: 'Persistent Swarm',
        text: 'Satellites last thirty-five seconds.',
        ultimate: { duration: 35, cooldown: 60 },
      },
      {
        id: 'hunter-killer',
        name: 'Hunter-Killer',
        text: 'Three satellites, triple damage each.',
        ultimate: { count: 3, damage: 48 },
        palette: { core: 0x1a1c24, feature: 0xb33951, featureLight: 0xff8fa3 },
      },
    ],
  },
};

const umbra: PlanetDef = {
  id: 'umbra',
  name: 'Umbra',
  designation: 'SOL-VIII · CLASS J (GAS)',
  epithet: 'Reposition the battlefield itself.',
  bio: 'A gas giant with a crystallised heart and an unreasonable amount of gravity. Umbra does not out-shoot a wave; it gathers one up and puts it somewhere convenient. The ring sits wider than anyone else can manage, and everything on it is slow to turn and worth the wait.',
  temperament: {
    restlessness: 0.4,
    jitter: 0.05,
    reactivity: 0.7,
    pulsePeriod: 8,
    traits: ['vast', 'patient', 'heavy'],
  },
  passive: {
    id: 'deep-well',
    label: 'Deep Well',
    text: 'Wider orbit ring, +2 ring mounts.',
    ringRadiusBonus: 26,
    extraRingSlots: 2,
  },
  ultimate: {
    id: 'singularity',
    name: 'Singularity',
    kind: 'singularity',
    cooldown: 60,
    aimed: true,
    text: 'Every bearing becomes one bearing. Hold them there and let the guns work.',
    damage: 0,
    spread: Math.PI * 2,
    duration: 3.5,
    groundDuration: 0,
    groundDps: 0,
    count: 1,
    knockback: 0,
    frontSpeed: 0,
  },
  exclusive: 'gravity-tether',
  slots: { surface: 3, ring: 3 },
  spin: 0.03,
  ringInertia: 1.6,
  coreHp: 130,
  palette: {
    // The crystal-heart read: pale rose shell, violet spines, near-black core.
    halo: 0xf6dced,
    crust: 0xe0aecb,
    crustDark: 0xbd85a9,
    crustLight: 0xf7dced,
    feature: 0x9c6f96,
    featureDark: 0x6e4a6b,
    featureLight: 0xf3d3e6,
    core: 0x50354f,
    coreRim: 0x36233a,
    accent: 0xd98ec4,
  },
  body: {
    feature: 'crystals',
    featureCount: 26,
    featureReach: 0.66,
    featureChaos: 0.55,
    coreRatio: 0.47,
    shellWobble: 0.03,
    shellLobes: 4,
    speckle: 0,
    halo: 0.2,
    bands: 3,
    face: 'ancient',
  },
  voice: {
    onWave: ['They approach the well.', 'Let them fall inward.', 'Gravity is patient.'],
    onUltimate: ['Down.', 'One bearing now.'],
    onHurt: ['A pinprick.', 'The mass endures.'],
    onClear: ['Swallowed.', 'The well is quiet.'],
  },
  evolutions: {
    l4: [
      {
        id: 'event-horizon',
        name: 'Event Horizon',
        text: 'Singularity also crushes: 40 damage per second while held.',
        ultimate: { damage: 40, groundDuration: 3.5, groundDps: 40 },
        palette: { core: 0x120a16, coreRim: 0x2a1030 },
        body: { coreRatio: 0.56, featureReach: 0.74 },
      },
      {
        id: 'tidal',
        name: 'Tidal',
        text: 'Held for six seconds instead of three and a half.',
        ultimate: { duration: 6, cooldown: 70 },
        body: { bands: 6, shellWobble: 0.08 },
      },
    ],
    l8: [
      {
        id: 'accretion',
        name: 'Accretion',
        text: 'Two more ring slots. The ring gets heavier still.',
        passive: { extraRingSlots: 4, ringRadiusBonus: 34 },
        body: { featureCount: 34 },
      },
      {
        id: 'collapse',
        name: 'Collapse',
        text: 'On release, everything gathered is flung back to the spawn ring.',
        ultimate: { knockback: 260, damage: 60 },
        palette: { feature: 0x6b4a86, featureLight: 0xc9a8f0, accent: 0xa98cf0 },
      },
    ],
  },
};

const kepler: PlanetDef = {
  id: 'kepler-9b',
  name: 'Kepler-9b',
  designation: 'K-09 · CLASS ∅ · UNSURVEYED',
  epithet: 'Pure orbital. Hard mode.',
  bio: 'The crust will not take a mounting. Whatever this thing was before, nothing bolts to it now — so the entire defence lives on the ring, and the ring is the only thing between a wave and a very old surface. Every wave is a rotation problem with no fallback.',
  temperament: {
    restlessness: 0.55,
    jitter: 0.3,
    reactivity: 0.9,
    pulsePeriod: 3.1,
    traits: ['austere', 'unhelpful', 'old'],
  },
  passive: {
    id: 'unbolted',
    label: 'Unbolted',
    text: 'No surface mounts. Everything lives in orbit.',
    noSurfaceSlots: true,
    extraRingSlots: 0,
  },
  ultimate: {
    id: 'ring-storm',
    name: 'Ring Storm',
    kind: 'ringstorm',
    cooldown: 46,
    aimed: false,
    text: 'The ring fires outward. Everywhere. For four seconds.',
    damage: 22,
    spread: Math.PI * 2,
    duration: 4,
    groundDuration: 0,
    groundDps: 0,
    count: 1,
    knockback: 0,
    frontSpeed: 0,
  },
  exclusive: '',
  // The three surface slots exist but `noSurfaceSlots` zeroes them — that's
  // what the Reforged branch at L8 gives back.
  slots: { surface: 2, ring: 4 },
  spin: 0.075,
  ringInertia: 1.05,
  coreHp: 85,
  palette: {
    // Dark by design, but the crust has to clear the void it sits on or the
    // silhouette disappears and the rifts read as floating sticks.
    halo: 0x322e4d,
    crust: 0x3a3654,
    crustDark: 0x24223a,
    crustLight: 0x565177,
    feature: 0x16151f,
    featureDark: 0x0c0b14,
    featureLight: 0xff6b35,
    core: 0x14131f,
    coreRim: 0xff6b35,
    accent: 0xff6b35,
  },
  body: {
    feature: 'vents',
    featureCount: 9,
    featureReach: 0.7,
    featureChaos: 0.65,
    coreRatio: 0.34,
    shellWobble: 0.11,
    shellLobes: 5,
    speckle: 0.12,
    halo: 0.12,
    bands: 0,
    face: 'hollow',
  },
  voice: {
    onWave: ['—', 'Bearings.', 'It notices.'],
    onUltimate: ['The ring answers.', '—'],
    onHurt: ['Old damage, made new.', '—'],
    onClear: ['Still here.', '—'],
  },
  evolutions: {
    l4: [
      {
        id: 'iron-halo',
        name: 'Iron Halo',
        text: 'A seventh ring slot opens.',
        passive: { extraRingSlots: 1 },
        body: { featureCount: 12 },
      },
      {
        id: 'emberline',
        name: 'Emberline',
        text: 'Ring Storm burns: half damage, but the ground stays lit.',
        ultimate: { damage: 11, groundDuration: 8, groundDps: 18 },
        palette: { crust: 0x4a2a2a, featureLight: 0xffb03a, coreRim: 0xffb03a },
      },
    ],
    l8: [
      {
        id: 'sixth-second',
        name: 'Sixth Second',
        text: 'Ring Storm runs for eight seconds.',
        ultimate: { duration: 8, cooldown: 64 },
      },
      {
        id: 'reforged',
        name: 'Reforged',
        text: 'The crust takes a mounting again: three surface slots.',
        passive: { noSurfaceSlots: false, text: 'Three surface slots recovered.' },
        palette: { crust: 0x3d3a56, crustLight: 0x6c6890, featureLight: 0xffd166 },
        body: { feature: 'plates', featureCount: 16, featureChaos: 0.3 },
      },
    ],
  },
};

export const PLANETS: readonly PlanetDef[] = [terra, ignis, glacia, machina, umbra, kepler];

/** v1 launch roster (§11). The rest are built and playable behind the unlock. */
export const V1_PLANETS = ['terra', 'ignis', 'machina'] as const;
